import { morphSection } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const DEBOUNCE_MS = 300;
const DRAWER_SECTION = 'cart-drawer-section';

/**
 * Única fuente de verdad del carrito en el cliente.
 *
 * Nadie más debe llamar a /cart/*.js: si dos sitios mutan el carrito por su
 * cuenta, las cantidades de las tarjetas se desincronizan sin forma de saberlo.
 *
 * Se expone en window.CasukiCart en vez de vía importmap para no tocar el
 * scripts.liquid de Horizon (se perdería en una actualización del tema).
 */
class CasukiCartStore extends EventTarget {
  #quantities = new Map(); // variantId (string) -> cantidad confirmada por el servidor
  #pending = new Map();    // variantId -> cantidad que el usuario acaba de pedir
  #timers = new Map();
  #tokens = new Map();     // variantId -> token monótono, para descartar respuestas viejas
  #itemCount = 0;
  #totalPrice = 0;     // total del carrito en unidades menores, según el servidor

  get itemCount() {
    return this.#itemCount;
  }

  /** Total confirmado por el servidor. La barra de envío gratis proyecta sobre esto. */
  get totalPrice() {
    return this.#totalPrice;
  }

  /** Lo que debe pintarse: la intención del usuario si la hay, si no la verdad del servidor. */
  quantityFor(variantId) {
    const key = String(variantId);
    return this.#pending.has(key) ? this.#pending.get(key) : this.#quantities.get(key) || 0;
  }

  isPending(variantId) {
    return this.#pending.has(String(variantId));
  }

  async hydrate() {
    try {
      const response = await fetch(`${ROOT}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) return;
      this.#absorb(await response.json());
    } catch {
      // Sin red, la página se queda con lo que pintó Liquid. Aceptable.
    }
  }

  /** Punto de entrada único desde la interfaz. */
  setQuantity(variantId, quantity) {
    const key = String(variantId);
    this.#pending.set(key, Math.max(0, quantity));
    this.#emit(); // la interfaz responde ya, sin esperar la red
    this.#schedule(key);
  }

  #schedule(key) {
    clearTimeout(this.#timers.get(key));
    this.#timers.set(key, setTimeout(() => this.#flush(key), DEBOUNCE_MS));
  }

  async #flush(key) {
    const desired = this.#pending.get(key);
    if (desired == null) return;

    const token = (this.#tokens.get(key) || 0) + 1;
    this.#tokens.set(key, token);

    try {
      const response = await fetch(`${ROOT}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates: { [key]: desired }, sections: [DRAWER_SECTION] }),
      });

      // Llegó tarde: el usuario ya pidió otra cosa. Descartar sin tocar nada.
      if (this.#tokens.get(key) !== token) return;
      if (!response.ok) throw new Error(String(response.status));

      const cart = await response.json();
      this.#pending.delete(key);
      this.#absorb(cart);
      this.#morphDrawer(cart);
    } catch {
      if (this.#tokens.get(key) !== token) return;
      this.#pending.delete(key);
      await this.hydrate(); // volver a la verdad del servidor
      this.dispatchEvent(new CustomEvent('casuki:cart-error', { detail: { variantId: key } }));
    }
  }

  #absorb(cart) {
    this.#quantities.clear();
    for (const item of cart.items) {
      const key = String(item.variant_id);
      // Una misma variante puede venir en varias líneas (propiedades distintas).
      this.#quantities.set(key, (this.#quantities.get(key) || 0) + item.quantity);
    }
    this.#itemCount = cart.item_count;
    this.#totalPrice = Number(cart.total_price) || 0;
    this.#emit();
  }

  #morphDrawer(cart) {
    const html = cart.sections && cart.sections[DRAWER_SECTION];
    if (!html) return;
    // Falla si el drawer no está en esta plantilla: no es un error.
    morphSection(DRAWER_SECTION, html).catch(() => {});
  }

  #emit() {
    this.dispatchEvent(new CustomEvent('casuki:cart-change'));
  }
}

const cartStore = new CasukiCartStore();

// El HTML ya viene con cantidades de Liquid; esto corrige si venía de caché.
cartStore.hydrate();

// Volver con el botón atrás sirve una página de bfcache con datos viejos.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) cartStore.hydrate();
});

// Si el carrito cambió en otra pestaña, corregir al volver a esta.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') cartStore.hydrate();
});

/**
 * La ficha de producto añade con el flujo nativo de Horizon ("Añadir al
 * carrito" postea a /cart/add.js), sin pasar por setQuantity(). Horizon
 * avisa de cualquier cambio de carrito con este evento estándar de la
 * plataforma; lo usamos para que las tarjetas y el contador del
 * encabezado también se enteren de esas altas.
 *
 * El evento se dispara ANTES de que el POST real termine (es la señal de
 * "esto va a pasar", no de "ya pasó"): rehidratar de inmediato lee el
 * carrito viejo. Por eso se espera event.promise, que Horizon resuelve
 * justo cuando el carrito ya quedó confirmado en el servidor.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const settled = event.promise && typeof event.promise.then === 'function' ? event.promise : Promise.resolve();
  settled.then(() => cartStore.hydrate(), () => cartStore.hydrate());
});

window.CasukiCart = window.CasukiCart || {};
window.CasukiCart.cartStore = cartStore;

export { cartStore };
